import React, { useEffect, useRef, useState } from "react";
import { useAppSelector } from "@/services/hooks";

// reactstrap components
import { Col, Label, Row } from "reactstrap";
import { ArrayLinksEditor, type LinkRow } from "./ContentArrayLinks";
import { optionsForContentVar, withLinkArray } from "../contentVariableHelpers";
import VariableField, {
  bindFieldProps,
  hasFieldFor,
  type FieldOverrides,
} from "../../variableFields/VariableField";
import { CONTENT_OVERRIDES } from "../../variableFields/contentOverrides";
import ContentArrayTextField from "../../variableFields/ContentArrayTextField";
import ContentArrayContentField from "../../variableFields/ContentArrayContentField";
import BottomBlockLeftField from "../../variableFields/BottomBlockLeftField";
import type { DropdownOption } from "../dropdownOptions";
import { useDropdownOptions, type PageTypeLike } from "./useDropdownOptions";
import { useOptionalDropdownOptions } from "./useOptionalDropdownOptions";

/**
 * HandleContent — the page-UPDATE screens' host for the complex
 * ContentRequired / ContentOptional variables (SCRUM-2167: one host with a
 * `status` prop where two near-identical components used to be). The basic
 * types (String, Number, Boolean, Link) are rendered inline by the screen.
 *
 * Each status has a thin inner host so its option hook is called
 * unconditionally; both render the same body with the status's config.
 */

// Static override maps on top of CONTENT_OVERRIDES. Module-level so the block
// identities can never churn with redux fetch responses — a fresh identity
// would remount the block subtree and drop input focus mid-typing. Dynamic
// per-render values (the pages/categories slices, the links handler) travel
// via VariableField's fieldProps instead.
const UPDATE_BASE_OVERRIDES: FieldOverrides = {
  ...CONTENT_OVERRIDES,
  // pinned: a single Product variable renders the Unknown-type fallback
  // on the update screens (only Array:product has an editor here)
  Product: null,
  "Array:text": ContentArrayTextField,
  "Array:content": ContentArrayContentField,
  "Array:links": ArrayLinksEditor,
};

// Optional-only 2-per-row raw-input layout for bottomblockleft (pinned
// deviation). Bound at module level so its identity is static.
const CompactBottomBlockLeftField = bindFieldProps(BottomBlockLeftField, {
  compact: true,
});

export type ContentStatus = "required" | "optional";

/** The seven pinned differences between the two statuses, as data. */
type StatusConfig = {
  variabletype: "ContentRequired" | "ContentOptional";
  overrides: FieldOverrides;
  /** Required re-selects its variables when page_to_update changes; Optional only on mount. */
  rederiveOnPageChange: boolean;
  /** Optional keeps Array:text edits local (never reported — pinned quirk). */
  arrayTextStaysLocal: boolean;
  /** Optional reports sitemap edits via onChangeContentOptional({target:{name,value}}). */
  sitemapViaContentOptional: boolean;
  /** Required sorts an Array:product value sponsored-first on write. */
  sortSponsoredFirst: boolean;
  /** Optional allowed deleting the last link row (isRequired was false). */
  linksRequired?: false;
  emptyMessage: string;
};

const STATUS_CONFIG: Record<ContentStatus, StatusConfig> = {
  required: {
    variabletype: "ContentRequired",
    overrides: UPDATE_BASE_OVERRIDES,
    rederiveOnPageChange: true,
    arrayTextStaysLocal: false,
    sitemapViaContentOptional: false,
    sortSponsoredFirst: true,
    emptyMessage: "No Required Content variables available...",
  },
  optional: {
    variabletype: "ContentOptional",
    overrides: { ...UPDATE_BASE_OVERRIDES, bottomblockleft: CompactBottomBlockLeftField },
    rederiveOnPageChange: false,
    arrayTextStaysLocal: true,
    sitemapViaContentOptional: true,
    sortSponsoredFirst: false,
    linksRequired: false,
    emptyMessage: "No Optional Content variables available...",
  },
};

/** One `page_content` entry: a content variable and its stored value. */
type ContentEntry = {
  variable?: string;
  variabletype?: string;
  type?: string;
  complextype?: string;
  value?: unknown;
};

export type HandleContentProps = {
  status: ContentStatus;
  page_to_update: {
    page_template?: {
      content_variables?: {
        ContentRequired?: { variable?: string }[];
        ContentOptional?: { variable?: string }[];
      };
    };
    page_content?: ContentEntry[];
    [field: string]: unknown;
  };
  /** Receives the whole updated variable object after an edit. */
  onVariableChange?: (variable: ContentEntry) => void;
  /** Optional's sitemap channel: `({ target: { name, value } })`. */
  onChangeContentOptional?: (e: { target: { name?: string; value: unknown } }) => void;
  pageType?: PageTypeLike;
};

type OptionSources = Record<
  "products" | "categories" | "subcategories" | "posts",
  DropdownOption<unknown>[]
>;

// Types the screen renders inline (UpdatePageView) — skipped here.
const BASIC_TYPES: (string | undefined)[] = ["String", "Number", "Boolean", "Link"];

/**
 * The page_content entries a status renders: its variabletype, not a basic
 * type, and present in the template's list for that status (an empty list
 * keeps every entry); Array values are normalised to arrays.
 */
const selectVariables = (
  page: HandleContentProps["page_to_update"],
  variabletype: StatusConfig["variabletype"]
): ContentEntry[] => {
  const templateVarNames = (page.page_template?.content_variables?.[variabletype] || []).map(
    (v) => v.variable
  );
  return (
    page.page_content?.filter(
      (entry) =>
        entry.variabletype === variabletype &&
        !BASIC_TYPES.includes(entry.type) &&
        (templateVarNames.length === 0 || templateVarNames.includes(entry.variable))
    ) || []
  ).map((v) => {
    if (v.type === "Array" && !Array.isArray(v.value)) {
      return v.complextype === "links" ? { ...v, value: [{ name: "", link: "" }] } : { ...v, value: [] };
    }
    return v;
  });
};

const sortSponsoredFirst = (value: unknown): unknown => {
  if (!Array.isArray(value) || !value[0]?.product) return value;
  return [...value].sort((x, y) =>
    x.product.sponsored === y.product.sponsored ? 0 : x.product.sponsored ? -1 : 1
  );
};

function ContentVariables({
  config,
  options,
  page_to_update,
  onVariableChange,
  onChangeContentOptional,
}: Omit<HandleContentProps, "status"> & { config: StatusConfig; options: OptionSources }) {
  const categories_redux = useAppSelector((state) => state.categories.categories);
  const pages_redux = useAppSelector((state) => state.pages.pages);

  // Which page_content entries this screen renders (local, edited in place).
  const [Variables, setVariables] = useState<ContentEntry[]>([]);
  const selected = useRef(false);
  useEffect(() => {
    if (selected.current && !config.rederiveOnPageChange) return;
    selected.current = true;
    setVariables(selectVariables(page_to_update, config.variabletype));
  }, [page_to_update, config]);

  const report = (updated: ContentEntry[], name: string) => {
    const updatedVariable = updated.find((v) => v.variable === name);
    if (updatedVariable && onVariableChange) onVariableChange(updatedVariable);
  };

  // One applier for every registry block, with the status's channel rules.
  const applyFieldValue = (contentVar: ContentEntry) => (name: string, value: unknown) => {
    const nextValue = config.sortSponsoredFirst ? sortSponsoredFirst(value) : value;
    const updated = Variables.map((v) => (v.variable === name ? { ...v, value: nextValue } : v));
    setVariables(updated);

    if (config.arrayTextStaysLocal && contentVar.type === "Array" && contentVar.complextype === "text") {
      return;
    }
    if (config.sitemapViaContentOptional && contentVar.type === "sitemap") {
      onChangeContentOptional?.({ target: { name, value: nextValue } });
      return;
    }
    report(updated, name);
  };

  // Array:links reports the whole updated variable like every other type
  // (SCRUM-643 review fix): the legacy handler wrote the shared page_content
  // entry in place, so the immutable rewrite must report or link edits are
  // silently lost at save.
  const commitLinkArray = (LinksArray: LinkRow[], variableName: string) => {
    const updated = withLinkArray(Variables, LinksArray, variableName);
    setVariables(updated);
    report(updated, variableName);
  };

  if (Variables.length === 0) {
    return <div>{config.emptyMessage}</div>;
  }
  return (
    <div>
      {Variables.map((contentVar) =>
        hasFieldFor(contentVar, config.overrides) ? (
          <VariableField
            key={contentVar.variable}
            settingVar={contentVar}
            options={optionsForContentVar(contentVar, options)}
            onChange={applyFieldValue(contentVar)}
            overrides={config.overrides}
            fieldProps={{
              pages: pages_redux,
              categories: categories_redux,
              linksHandler: commitLinkArray,
              ...(config.linksRequired === false ? { linksRequired: false } : {}),
            }}
          />
        ) : (
          // Fallback for unknown types - show variable name and type
          <div key={contentVar.variable} className="mb-3">
            <Row>
              <Label md="2" className="form-control-label">
                {contentVar.variable}
              </Label>
              <Col md="10">
                <span className="text-warning">
                  Unknown type: {contentVar.type}
                  {contentVar.complextype ? ` (${contentVar.complextype})` : ""}
                </span>
              </Col>
            </Row>
          </div>
        )
      )}
    </div>
  );
}

const toSources = (o: ReturnType<typeof useOptionalDropdownOptions>): OptionSources => ({
  products: o.localProducts,
  categories: o.localCategories,
  subcategories: o.localSubCategories,
  posts: o.localPosts,
});

function RequiredHost({ pageType, ...props }: Omit<HandleContentProps, "status">) {
  const options = useDropdownOptions({ page_to_update: props.page_to_update, pageType });
  return <ContentVariables {...props} config={STATUS_CONFIG.required} options={toSources(options)} />;
}

function OptionalHost({ pageType, ...props }: Omit<HandleContentProps, "status">) {
  const options = useOptionalDropdownOptions(pageType);
  return <ContentVariables {...props} config={STATUS_CONFIG.optional} options={toSources(options)} />;
}

function HandleContent({ status, ...props }: HandleContentProps) {
  return status === "required" ? <RequiredHost {...props} /> : <OptionalHost {...props} />;
}

export default HandleContent;
